import { useCallback, useMemo, useState } from "react"
import { useStaticQuery, graphql } from "gatsby"

import { useCore } from "@app/hooks/useCore"
import { useFunctions } from "@app/hooks/useFunctions"
import { useConfigContext } from "@app/providers/config"

type Data = {
  [key: string]: string
}

type UseForm = {
  data: Data
  errors: Array<string>
  form: GatsbyTypes.SanitySettingForms | null
  handleChange: (event: React.BaseSyntheticEvent) => void
  handleReset: () => void
  handleSubmit: (event: React.BaseSyntheticEvent) => Promise<void>
  loading: boolean
  setData: React.Dispatch<React.SetStateAction<Data>>
  sent: boolean
  setTurnstileToken: React.Dispatch<React.SetStateAction<string>>
}

const INITIAL_STATE = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  type: "",
  option: "",
  message: "",
}

export const useForm = (rawInitialState?: Data, handle?: string, dynamicRecipient?: boolean): UseForm => {
  const {
    services: { location },
  } = useConfigContext()
  const {
    helpers: { edgeNormaliser },
  } = useCore()
  const { callFunction } = useFunctions()
  const { forms } = useStaticQuery<GatsbyTypes.StaticFormsQuery>(graphql`
    query StaticForms {
      forms: allSanitySettingForms {
        edges {
          node {
            id: _id
            title
            handle {
              current
            }
            subject
            types {
              title
              recipients
              emailTemplate
              options
            }
          }
        }
      }
    }
  `)

  const initialState = useMemo(() => rawInitialState || INITIAL_STATE, [rawInitialState])

  const [data, setData] = useState<Data>(initialState)
  const [errors, setErrors] = useState<Array<string>>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [sent, setSent] = useState<boolean>(false)
  const [turnstileToken, setTurnstileToken] = useState<string>("")

  const form = useMemo(
    () =>
      (edgeNormaliser(forms) as Array<GatsbyTypes.SanitySettingForms>)?.find(form =>
        form?.handle?.current === handle
          ? {
              ...form,
              handle: form?.handle?.current,
              types: Object.assign({}, ...(form?.types?.map(type => ({ [type?.title?.toLowerCase() || ""]: { ...type } })) || [])),
            }
          : false
      ) || null,
    [edgeNormaliser, forms, handle]
  )

  const handleSubmit = useCallback(
    async (event: React.BaseSyntheticEvent) => {
      event.preventDefault()
      setLoading(true)
      setErrors([])

      if (!(form && form.types?.length > 0)) {
        setErrors(["No matching form found"])
        setLoading(false)

        return
      }

      const formData: Data = {
        ...data,
        email: !dynamicRecipient ? data.email : "",
        to: dynamicRecipient ? data.email : "",
      }
      const id = form.id
      const type = (formData?.type ? form.types[formData.type.toLowerCase() || ""] : form.types[0])?.title?.toLowerCase()
      const subject = form.subject || `Form submission from ${form.title}`
      const response = await fetch(location.serviceUrl)
      const userData = await response.json()

      const { status, body } = await callFunction("form-submission", {
        id,
        type,
        subject,
        data: formData,
        ipData: userData,
        userAgent: navigator.userAgent || "",
        ["cf-turnstile-token"]: turnstileToken,
      })

      if (status === "error") setErrors([body?.toString()])
      if (status === "success") setSent(true)

      setLoading(false)
    },
    [form, data, dynamicRecipient, location.serviceUrl, callFunction, turnstileToken]
  )

  const handleChange = useCallback(
    ({ target: { type, name, value, checked } }: React.BaseSyntheticEvent) => {
      setData(prevState => ({
        ...prevState,
        [name]: type === "checkbox" ? checked : value,
      }))
    },
    [setData]
  )

  const handleReset = useCallback(() => {
    setData(initialState)
    setErrors([])
    setSent(false)
  }, [initialState, setData, setErrors, setSent])

  return { data, errors, form, handleChange, handleReset, handleSubmit, loading, setData, sent, setTurnstileToken }
}
